// ─── Site Metadata ────────────────────────────────────────────────────────────
export const SITE = {
  name: "Glolex Business Solutions",
  shortName: "GLOLEX",
  tagline: "Simplifying Global Trade",
  subLabel: "Customs | Trade Compliance | Supply Chain",
  description:
    "Glolex Business Solutions — India's specialist customs and trade advisory firm. FTA utilisation, duty drawback, MOOWR, AEO, BIS, EPR, DGFT licensing, HS classification, and global offshoring services.",
  url: "https://www.glolexbs.com",
  email: "connect@glolex.co.in",
  phone: "+91 73582 54459",
  social: {
    linkedin: "https://www.linkedin.com/company/glolex-business-solutions/",
    twitter: "https://x.com/glolex_in",
    youtube: "https://youtube.com/@glolexbs",
    whatsapp: "https://wa.me/917358254459",
  },
};

// ─── Navigation Links ─────────────────────────────────────────────────────────
export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Services", path: "/services", hasDropdown: true },
  { label: "Industries", path: "/industries" },
  { label: "Insights", path: "/insights" },
  { label: "Contact Us", path: "/contact" },
];

// ─── Services Data ────────────────────────────────────────────────────────────
export const SERVICES = [
  {
    id: "customs-trade-compliance",
    slug: "customs-trade-compliance",
    icon: "🛃",
    title: "Customs & Trade Compliance",
    shortDesc:
      "End-to-end customs compliance management — from import/export procedures to regulatory filings and port clearance.",
    description:
      "We provide comprehensive customs compliance advisory covering import/export procedures, customs valuation, classification reviews, audit defence and representation before customs authorities.",
    features: [
      "Import & Export Compliance Audit",
      "Customs Valuation Advisory",
      "Regulatory Filing Support",
      "Audit Defence & Representation",
    ],
    whatWeDo: [
      "Customs compliance health-checks and audits",
      "Import/export licensing and documentation review",
      "Customs valuation and transaction value structuring",
      "Representation before customs authorities",
      "Compliance training for import/export teams",
    ],
    impact: [
      "Reduced risk of duty demands and penalties",
      "Faster customs clearance cycles",
      "Audit-ready compliance posture",
      "Lower cost of customs operations",
    ],
  },
  {
    id: "duty-optimisation",
    slug: "duty-optimisation",
    icon: "💰",
    title: "Duty Optimisation",
    shortDesc:
      "Identify and unlock legal duty savings through classification, FTA utilisation, schemes and structuring.",
    description:
      "Our duty optimisation practice systematically reviews your import and export footprint to identify every legal avenue for duty reduction — from classification reviews to scheme utilisation and FTA benefits.",
    features: [
      "Duty Savings Identification",
      "Classification Optimisation",
      "FTA Benefit Unlocking",
      "Scheme Utilisation Review",
    ],
    whatWeDo: [
      "Full duty liability mapping",
      "Customs scheme utilisation review (MOOWR, EOU, AEO)",
      "FTA eligibility and benefit analysis",
      "HS classification optimisation",
      "Prospective and retrospective duty savings analysis",
    ],
    impact: [
      "Material reduction in customs duty outflow",
      "Improved import cost competitiveness",
      "Identified savings typically 5–25% of duty spend",
      "Legitimate and auditable duty reduction framework",
    ],
  },
  {
    id: "free-trade-agreements",
    slug: "free-trade-agreements",
    icon: "🌐",
    title: "Free Trade Agreements",
    shortDesc:
      "Maximise FTA duty savings through eligibility assessment, Rules of Origin analysis and Certificate of Origin advisory.",
    description:
      "India is party to over 13 active FTAs and several preferential trade arrangements. Yet the majority of eligible importers and exporters leave significant duty savings on the table because FTA utilisation is poorly understood and improperly executed.",
    features: [
      "FTA Eligibility Assessment",
      "Rules of Origin Analysis",
      "Certificate of Origin Advisory",
      "FTA Utilisation Audits",
    ],
    whatWeDo: [
      "FTA eligibility assessment — ASEAN, SAFTA, India-UAE CEPA, India-Australia ECTA, India-Mauritius CECPA, and bilateral agreements",
      "Rules of Origin (RoO) analysis — Tariff Change Criteria, Value Addition tests and Specific Process rules",
      "Certificate of Origin (CoO) advisory, documentation and management",
      "FTA utilisation audits — identifying missed savings on past imports",
      "Supplier declarations and back-to-back CoO structuring",
      "Proactive monitoring of evolving FTA negotiations — India-EU, India-GCC, India-UK",
      "Team training on FTA compliance requirements",
    ],
    businessImpact:
      "Clients typically achieve 4–15% duty savings on eligible product categories. For high-volume importers, even partial FTA utilisation can yield savings in millions annually.",
    impact: [
      "4–15% duty savings on eligible product categories",
      "Compliant FTA benefit claims",
      "Reduced risk of origin fraud or CoO rejection",
      "Competitive landed cost improvement",
    ],
  },
  {
    id: "temporary-importation",
    slug: "temporary-importation",
    icon: "🔄",
    title: "Temporary Importation",
    shortDesc:
      "Structure temporary import arrangements — ATA Carnets, Import under Bond, Bank Guarantee optimisation and re-importation planning.",
    description:
      "Goods imported for exhibitions, testing, repairs, processing, or re-export do not always need to attract full customs duty. GLOLEX helps Companies structure temporary importation correctly — saving duty and ensuring seamless re-export.",
    features: [
      "ATA Carnet Advisory",
      "Import under Bond Documentation",
      "Bank Guarantee Optimisation",
      "Re-importation Planning",
    ],
    whatWeDo: [
      "ATA Carnet advisory for professional equipment, samples, and exhibition goods",
      "Temporary imports structuring and compliance",
      "Re-importation duty exemption planning",
      "Import under Bond documentation and bank guarantee optimisation",
      "Monitoring and closure of pending temporary import bonds",
    ],
    impact: [
      "Zero duty outflow on qualifying temporary imports",
      "Reduced bank guarantee exposure",
      "Streamlined bond closure process",
      "Compliant re-export documentation",
    ],
  },
  {
    id: "duty-drawback",
    slug: "duty-drawback",
    icon: "↩️",
    title: "Duty Drawback",
    shortDesc:
      "Recover customs duties on re-exported goods through AIR drawback, Brand Rate fixation and IGST refund coordination.",
    description:
      "India's Duty Drawback Scheme allows exporters to recover customs duty paid on imported inputs used in exported products. Many exporters claim at sub-optimal rates or miss the scheme entirely.",
    features: [
      "AIR Drawback Optimisation",
      "Brand Rate Fixation",
      "IGST Refund Coordination",
      "Retrospective Export Review",
    ],
    whatWeDo: [
      "All-Industry Rate (AIR) drawback optimisation and review",
      "Brand Rate fixation applications where AIR is inadequate",
      "Drawback claims for re-exports",
      "IGST refund and drawback co-ordination",
      "Reconciliation of claims with export data and shipping bills",
      "Handling drawback disputes, SCNs and representations before customs authorities",
      "Retrospective review of past exports to identify unclaimed or under-claimed drawback",
    ],
    impact: [
      "Recovery of previously unclaimed duties",
      "Optimised drawback rates vs. AIR",
      "Faster refund processing",
      "Dispute resolution and claim approval",
    ],
  },
  {
    id: "tariff-engineering",
    slug: "tariff-engineering",
    icon: "⚙️",
    title: "Tariff Engineering",
    shortDesc:
      "Legal structuring of product composition, presentation or import configuration for favourable customs classification.",
    description:
      "Tariff engineering is the legal practice of structuring products, their composition, presentation, or import configuration so they attract the most favourable customs duty classification. It is one of the most powerful and most under-utilised tools available to importers.",
    features: [
      "Pre-import Tariff Analysis",
      "Product Composition Advisory",
      "Advance Ruling Liaison",
      "Multi-jurisdictional Planning",
    ],
    whatWeDo: [
      "Pre-import tariff position analysis and 'what-if' duty modelling",
      "Product composition and presentation advisory to achieve preferential classification",
      "Split shipment and component vs. assembled goods analysis",
      "Liaison with CBIC for advance rulings on classification positions",
      "Multi-jurisdictional tariff engineering for goods moving through multiple countries",
      "Risk review to distinguish lawful tariff planning from misdeclaration",
    ],
    impact: [
      "Reduced effective duty rate on product imports",
      "Documented and auditable classification position",
      "Advance ruling certainty",
      "Competitive cost advantage through legal structuring",
    ],
  },
  {
    id: "moowr",
    slug: "moowr",
    icon: "🏭",
    title: "MOOWR Advisory",
    shortDesc:
      "End-to-end MOOWR scheme advisory — feasibility, licensing, compliance and duty deferment for manufacturing in bonded warehouses.",
    description:
      "The MOOWR Regulations, 2019 offer one of the most powerful duty deferment mechanisms available under Indian Customs Law. Yet it remains significantly under-utilised — either because companies are unaware or because implementation is seen as complex.",
    description2:
      "Under MOOWR, a manufacturer can import goods into a customs-bonded warehouse and carry out manufacturing or value-addition operations without paying customs duty at the time of import. Duty is deferred and in many cases, eliminated entirely if the finished goods are exported.",
    features: [
      "Feasibility Assessment",
      "End-to-End Licensing",
      "Duty Deferment Modelling",
      "Monthly Compliance",
    ],
    whatWeDo: [
      "Feasibility assessment: Is your manufacturing setup MOOWR-eligible?",
      "End-to-end application and licensing — from site survey to customs approval",
      "Warehouse design advisory — record-keeping, custody and segregation requirements",
      "Duty deferment modelling: cash flow and working capital impact analysis",
      "Monthly compliance: B-17 bond management, waste and scrap accounting",
      "Integration with GST, drawback and FTP incentive schemes",
      "Representation before the Principal Commissioner during inspections and audits",
      "Scheme transition advisory for existing EOU / SEZ units considering MOOWR",
    ],
    benefits: [
      { title: "Zero Upfront Duty", desc: "Defer customs duty and IGST on imported inputs until clearance into DTA." },
      { title: "Full Duty Waiver on Export", desc: "Finished goods exported attract zero duty on imported inputs." },
      { title: "Cash Flow Advantage", desc: "Eliminates working capital lock-in from upfront duty payment." },
      { title: "No Geographic Restriction", desc: "Unlike SEZs, set up within your existing factory premises." },
      { title: "Flexible Scheme", desc: "Compatible with FTP benefits and GST refund mechanisms." },
      { title: "Competitive Pricing Edge", desc: "Enables more competitive export bids by eliminating embedded duty costs." },
    ],
    impact: [
      "Zero upfront duty on imported inputs",
      "Full duty waiver on exports",
      "Significant cash flow advantage",
      "No geographic restriction on warehouse location",
    ],
  },
  {
    id: "export-control-scomet",
    slug: "export-control-scomet",
    icon: "🔒",
    title: "Export Control & SCOMET Compliance",
    shortDesc:
      "SCOMET classification, export licence applications, TCCP design and multi-jurisdictional export control compliance.",
    description:
      "Export controls are among the most rapidly evolving and most consequential areas of Trade Law. Non-compliance can result in licence revocations, severe penalties, withdrawal of Trade privileges and lasting reputational damage.",
    description2:
      "India's SCOMET list controls the export of dual-use goods and technologies. Administered by DGFT in co-ordination with MEA and security agencies, SCOMET compliance requires specialist knowledge.",
    features: ["SCOMET Classification", "Export Licence Application", "TCCP Design", "End-use Screening"],
    whatWeDo: [
      "SCOMET classification — determining whether your product/technology falls on the SCOMET list",
      "End-use and end-user screening advisory",
      "Export Licence application and management (DGFT)",
      "Catch-all provision analysis",
      "Trade Control Compliance Programme (TCCP) design and implementation",
      "Internal audit frameworks for export-controlled shipments",
      "Multi-jurisdictional analysis — US EAR, EU Dual-Use Regulation, Wassenaar Arrangement",
      "Technology transfer compliance: ITAR, EAR, and Indian counterpart obligations",
      "Training for sales, logistics, and procurement teams",
    ],
    impact: [
      "Compliant export of controlled items",
      "Reduced risk of export control violations",
      "Documented TCCP for regulatory audits",
      "Confident market expansion with dual-use products",
    ],
  },
  {
    id: "aeo",
    slug: "aeo",
    icon: "✅",
    title: "AEO — Authorised Economic Operator Programme",
    shortDesc:
      "End-to-end AEO certification support — gap analysis, documentation, CBIC liaison, and post-certification maintenance.",
    description:
      "India's AEO Programme, administered by CBIC, is the cornerstone of India's trade facilitation architecture. AEO certification signals to customs authorities — both in India and in partner countries with MRAs — that your business is a trusted trader.",
    features: ["Gap Analysis", "Documentation & Application", "CBIC Liaison", "Post-certification Maintenance"],
    tiers: [
      { name: "AEO-T1", desc: "Basic tier for importers/exporters. Benefits: direct port delivery, fast-track clearance." },
      { name: "AEO-T2", desc: "Enhanced benefits: deferred duty payment, priority examination. Requires audit of internal controls." },
      { name: "AEO-T3", desc: "Highest tier: self-assessment, green channel clearance, global MRA recognition." },
      { name: "AEO-LO", desc: "Logistics Operators: CHAs, carriers, warehouses, freight forwarders." },
    ],
    whatWeDo: [
      "Gap analysis against AEO eligibility criteria",
      "Documentation: internal control manual, compliance history",
      "Application preparation, submission and CBIC liaison",
      "Facility inspection preparedness and dry-run audits",
      "Post-certification maintenance and annual review",
      "MRA benefit utilisation advisory",
      "AEO suspension / revocation response",
    ],
    impact: [
      "Faster customs clearance with reduced examinations",
      "MRA benefits for international trade",
      "Enhanced credibility with customs authorities",
      "Reduced bank guarantees and security requirements",
    ],
  },
  {
    id: "bis-compliance",
    slug: "bis-compliance",
    icon: "📋",
    title: "BIS — Bureau of Indian Standards Compliance",
    shortDesc:
      "BIS applicability screening, CRS registration, FMCS advisory, and port detention resolution for electronics and regulated products.",
    description:
      "The BIS certification regime has expanded significantly, covering electronics, toys, chemicals, footwear, steel and many other categories. Non-BIS-compliant goods can be seized at the port of entry.",
    features: ["Applicability Screening", "CRS Registration", "BIS FMCS Advisory", "Port Detention Resolution"],
    whatWeDo: [
      "Applicability screening: mandatory vs. voluntary certification for your product",
      "Compulsory Registration Scheme (CRS) for electronics — application and renewal",
      "Application, Audit preparation and BIS office liaison",
      "BIS FMCS (Foreign Manufacturers Certification Scheme) for overseas suppliers",
      "Product testing coordination with NABL / BIS-recognised laboratories",
      "Handling BIS compliance notices, show-cause notices and port detention advisories",
      "Compliance monitoring: tracking amendments to BIS mandatory order schedules",
      "BIS self-declaration advisory where applicable",
    ],
    impact: [
      "Compliant product entry into India",
      "Resolution of port detention issues",
      "Proactive QCO monitoring",
      "Reduced risk of non-compliant shipments",
    ],
  },
  {
    id: "hs-classification",
    slug: "hs-classification",
    icon: "🔢",
    title: "HS Classification Services",
    shortDesc:
      "Expert HS classification opinions, advance ruling applications, multi-jurisdictional classification and ERP advisory.",
    description:
      "The Harmonised System (HS) classification of goods is the foundation of Customs Compliance. Get it wrong and every downstream decision — Duty rates, FTA eligibility, Export Control applicability, Import Licensing — is built on a flawed base.",
    features: ["Classification Opinions", "Advance Ruling Applications", "Multi-jurisdictional Classification", "Classification Audit"],
    whatWeDo: [
      "Individual product classification opinions with written reasoning and legal basis",
      "Advance Ruling applications before CAAR (Customs Authority for Advance Rulings)",
      "Classification review for entire product catalogues — identifying misclassification risk",
      "Multi-jurisdictional classification — EU, US, UK, and ASEAN markets",
      "Classification for new and emerging products: EVs, semiconductors, AI hardware, biotech",
      "GRI analysis and Chapter Note / Section Note advisory",
      "CTH and HS 2022 amendment reconciliation",
      "Classification database maintenance for large Importers — ERP integration advisory",
    ],
    classificationAudit:
      "A structured review of all product classifications used in the previous 2–3 years, benchmarked against WCO guidance, CBIC circulars and AAR/CAAR precedents. Most audits surface both compliance risk and refund opportunities.",
    impact: [
      "Correct duty rate application",
      "Advance ruling certainty for import planning",
      "Reduced risk of mis-classification penalties",
      "Audit-ready classification database",
    ],
  },
  {
    id: "epr-compliance",
    slug: "epr-compliance",
    icon: "♻️",
    title: "EPR — Extended Producer Responsibility Compliance",
    shortDesc:
      "Extended Producer Responsibility compliance — CPCB registration, annual targets, recycler agreements and EPR credit advisory.",
    description:
      "Extended Producer Responsibility (EPR) is one of the most rapidly expanding compliance obligations for Importers, Manufacturers and Brand Owners in India. Mandated by the Ministry of Environment, Forest and Climate Change (MoEFCC) through CPCB (Central Pollution Control Board), EPR holds Producers and Importers legally responsible for the end-of-life management of their products and packaging.",
    description2:
      "Non-compliance attracts significant penalties, port-level clearance holds and can result in product import bans. GLOLEX Business Solutions offers a full EPR Compliance Service across all regulated categories.",
    features: ["CPCB Portal Registration", "Annual Target Calculation", "Recycler/Dismantler Agreements", "EPR Credit Advisory"],
    eprCategories: [
      { name: "Plastic Packaging (PPRW Rules)", desc: "Registration, targets, collection planning, recycler tie-ups, and annual reporting to CPCB under Plastic Waste Management Rules." },
      { name: "E-Waste (EWM Rules)", desc: "Producer registration, collection target compliance, authorised dismantler / recycler linkages and filing under E-Waste Management Rules." },
      { name: "Batteries (BMW Rules)", desc: "EPR registration for battery importers and manufacturers — primary, secondary, EV batteries — under Battery Waste Management Rules, 2022." },
      { name: "Tyres (HWMR)", desc: "Tyre importer and manufacturer EPR compliance under Hazardous and Other Wastes Management Rules." },
      { name: "Extended Categories", desc: "Advisory on emerging EPR obligations for new product categories as MoEFCC expands the regulatory perimeter." },
    ],
    whatWeDo: [
      "EPR applicability assessment — are you a Producer, Importer, or Brand Owner (PIBO) under any regulated category?",
      "Registration on the CPCB EPR portal — end-to-end application, documentation and submission",
      "Annual EPR target calculation — based on Import / Sales volumes and Regulatory Schedules",
      "Authorised Recycler / Dismantler identification and Agreement structuring",
      "EPR credit purchase and marketplace advisory",
      "Quarterly and annual compliance filings and reporting to CPCB",
      "Coordination with State Pollution Control Boards (SPCBs) for state-level compliance",
      "Monitoring of regulatory amendments — Targets, Schedules, and Penalty Structures",
      "Response to CPCB notices, Representations, and Compliance Hearings",
    ],
    eprCritical:
      "CPCB has significantly tightened enforcement of EPR obligations since 2022, with active monitoring of port-level imports. Importers without valid EPR registration and current-year targets set are facing goods being held at customs. For consumer goods importers, electronics companies, battery suppliers, and FMCG brands, EPR is no longer a back-office compliance item — it is a front-line clearance requirement.",
    impact: [
      "Full EPR regulatory compliance",
      "Optimised EPR credit procurement",
      "Reduced CPCB notice and penalty risk",
      "Efficient annual target fulfilment",
    ],
  },
  {
    id: "dgft-licensing",
    slug: "dgft-licensing",
    icon: "📜",
    title: "DGFT — Directorate General of Foreign Trade Licensing",
    shortDesc:
      "Complete DGFT licensing services — Advance Authorisation, EPCG, RoDTEP, DFIA, export obligation management and DGFT representation.",
    description:
      "The Directorate General of Foreign Trade (DGFT) administers India's Foreign Trade Policy (FTP) and a wide array of Import-Export Licences, Authorisations, and Incentive Schemes. Navigating the DGFT requirements, understanding applicable notifications and correctly structuring licence applications requires specialist knowledge and errors can result in forfeiture of valuable entitlements or regulatory exposure.",
    description2:
      "GLOLEX Business Solutions offers end-to-end DGFT licensing and FTP advisory services, covering the full spectrum of authorisations and schemes.",
    features: ["Advance Authorisation", "EPCG Licensing", "RoDTEP & RoSCTL", "Export Obligation Management"],
    sections: [
      {
        heading: "Import Licences & Authorisations",
        items: [
          "Advance Authorisation (AA) — duty-free import of inputs for Export production",
          "EPCG (Export Promotion Capital Goods) — duty-free capital goods import against export obligation",
          "Import Licence for Restricted Items — canalized goods, SCOMET, hazardous materials",
          "STE (State Trading Enterprise) routing advisory for canalised goods",
        ],
      },
      {
        heading: "Export Incentive Schemes",
        items: [
          "Remission of Duties and Taxes on Exported Products (RoDTEP) — rate advisory and claim filing",
          "Rebate of State and Central Taxes and Levies (RoSCTL) — for garment and made-up exporters",
          "Status Certificate applications — One Star to Five Star Export House",
          "Duty Drawback co-ordination with DGFT incentive stack",
        ],
      },
      {
        heading: "Special Schemes & Authorisations",
        items: [
          "DFIA (Duty Free Import Authorisation) — post-export duty-free input import",
          "Gem & Jewellery REP Licence and Replenishment Licence",
          "Sports goods and handloom sector authorisations",
          "Annual Advance Authorisation (AAA) for ongoing exporters",
          "Deemed Exports authorisation advisory — supplies to EOU, EPCG holders, power projects",
        ],
      },
      {
        heading: "Compliance & Obligation Management",
        items: [
          "Export Obligation fulfilment tracking and EODC (Export Obligation Discharge Certificate) filing",
          "Block-wise export obligation monitoring and extension applications",
          "Redemption of Advance Authorisation and EPCG licences",
          "Regularisation of past licences — lapsed obligations, pending redemptions",
          "DGFT adjudication and compounding representation",
          "SION (Standard Input Output Norms) review and Norms Committee representation",
        ],
      },
    ],
    dgftWhyUs:
      "The FTP 2023 introduced significant changes to scheme structures, eligibility criteria, and compliance timelines. Our team tracks every policy notification, trade notice and public notice issued by DGFT — ensuring our clients benefit from new entitlements as soon as they become available and are not caught offside by compliance changes.",
    impact: [
      "Duty-free input procurement for exporters",
      "EPCG-linked capital goods import savings",
      "RoDTEP scrip monetisation",
      "Export obligation compliance and closure",
    ],
  },
  {
    id: "government-agencies",
    slug: "government-agencies",
    icon: "🏛️",
    title: "Government Agencies — Licensing & Regulatory Support",
    shortDesc:
      "Single-window support for FSSAI, CDSCO, PESO, WPC, MoEF/CPCB, quarantine and other regulatory agency approvals.",
    description:
      "Importing and exporting in India increasingly intersects with a complex web of government agency requirements that go well beyond customs and DGFT. A single product may require clearances from up to five or more agencies before it can legally enter or leave the country. GLOLEX Business Solutions offers a consolidated, single-window advisory and execution service across all major government agency requirements.",
    features: ["FSSAI & CDSCO Clearances", "PESO & WPC Approvals", "Quarantine & MoEF Support", "Port-level Intervention"],
    agencyGrid: [
      { name: "FSSAI", full: "Food Safety and Standards Authority of India", icon: "🍽️" },
      { name: "CDSCO", full: "Central Drugs Standard Control Organisation", icon: "💉" },
      { name: "PESO", full: "Petroleum and Explosives Safety Organisation", icon: "⚗️" },
      { name: "WPC", full: "Wireless Planning and Coordination Wing (DoT)", icon: "📡" },
      { name: "MOEF / CPCB", full: "Ministry of Environment and CPCB", icon: "🌿" },
      { name: "Animal & Plant Quarantine", full: "NPPO / DAHD", icon: "🌾" },
      { name: "IRDAI / RBI / SEBI", full: "Trade Finance Interface", icon: "🏦" },
    ],
    singleWindowNote:
      "Most importers and exporters struggle to track which agency clearances are required for their specific product, country of origin, and end-use. GLOLEX Business Solutions provides a government agency clearance matrix for every new import program — mapping out every required approval, typical timeline, documentation and fee before the first shipment. This eliminates costly surprises at the port of entry.",
    whatWeDo: [
      "FSSAI import clearance and registration",
      "CDSCO drug and device licensing",
      "PESO licensing for hazardous goods",
      "WPC clearance for wireless equipment",
      "MoEF/CPCB environmental clearances",
      "Animal and plant quarantine clearances",
      "IRDAI/RBI/SEBI regulatory support",
      "Port-level intervention and release",
    ],
    serviceCards: [
      { title: "Clearance Matrix", desc: "Product-specific mapping of all agency requirements before your first shipment arrives." },
      { title: "Application Execution", desc: "We prepare, file and follow up on all agency licence applications — you don't need separate consultants for each agency." },
      { title: "Renewal Management", desc: "Proactive calendar management ensuring no licence lapses mid-supply-chain." },
      { title: "Port Intervention", desc: "When goods are held pending agency clearance, we provide rapid-response support to minimise demurrage." },
    ],
    impact: [
      "Faster regulatory clearances",
      "Reduced port detention and demurrage",
      "Single-window compliance management",
      "Proactive renewal and monitoring",
    ],
  },
  {
    id: "offshoring",
    slug: "offshoring",
    icon: "🌍",
    title: "Offshoring of Customs & Trade Operations",
    shortDesc:
      "Dedicated trade compliance teams for global companies — HS classification, FTA review, denied party screening and GTM support.",
    description:
      "As trade compliance complexity rises globally, so does the cost of maintaining an expert in-house team. GLOLEX Business Solutions offers a fully managed, India-based Customs and Trade intelligence function that embeds seamlessly with your global operations.",
    features: ["Dedicated Trade Desk", "HS Classification Management", "Denied Party Screening", "GTM Implementation Support"],
    whatWeDo: [
      "HS Classification management — daily, weekly, or as-needed classification of import/export SKUs",
      "FTA eligibility review and CoO documentation management",
      "Customs Duty calculations, Tariff schedule monitoring and Duty rate database maintenance",
      "Trade compliance calendar management — filing deadlines, Licence renewals, Regulatory updates",
      "Import and Export documentation review and pre-shipment compliance checks",
      "Customs Broker Management and performance monitoring",
      "Denied Party Screening (DPS) and sanctions compliance checks",
      "CBAM Compliance Support",
      "Centralized, enterprise-wide system to track global trade licences",
      "Preparation of compliance reports for internal audit and board-level governance",
      "Support for GTM system implementations — SAP GTS, OCR, Descartes",
    ],
    engagementModels: [
      { title: "Dedicated Desk", desc: "A named team works exclusively on your account, integrating with your internal systems." },
      { title: "Shared Expert Pool", desc: "Access to a specialist team on a retainer — ideal for growing companies." },
      { title: "Project-Based", desc: "Specific deliverables: classification audits, FTA projects, AEO applications." },
      { title: "Hybrid Model", desc: "Onshore client-facing leads with an India-based delivery hub." },
    ],
    whyIndia: [
      { title: "Cost Advantage", desc: "60–70% cost reduction vs. equivalent UK, US, EU, or Australian in-house teams" },
      { title: "Time Zone", desc: "IST overlaps with morning Asia and morning Europe — ideal for global ops" },
      { title: "Language", desc: "English-fluent professionals across all service lines" },
      { title: "Regulatory Depth", desc: "Unmatched expertise in India-specific trade law; strong international trade knowledge" },
      { title: "Data Security", desc: "ISO 27001-aligned practices; NDA and data processing agreements as standard" },
      { title: "Scalability", desc: "Ramp up or down based on business cycles — no long-term headcount commitment" },
    ],
    impact: [
      "60–70% cost reduction vs. in-house team",
      "Time zone and language advantage",
      "Scalable engagement model",
      "Data-secure, process-driven delivery",
    ],
  },
];

// ─── Stats ────────────────────────────────────────────────────────────────────
export const STATS = [
  { value: "₹500 Cr+", label: "Duty savings identified" },
  { value: "15+", label: "Countries covered under FTA advisory" },
  { value: "200+", label: "HS Classification mandates" },
  { value: "50+", label: "Govt. licences secured" },
];

// ─── Testimonials ─────────────────────────────────────────────────────────────
export const TESTIMONIALS = [
  {
    quote:
      "Glolex Business Solutions identified ₹4.2 crore in unclaimed drawback from our last three years of exports. The ROI on their engagement was clear within the first month.",
    author: "CFO",
    role: "Mid-size Pharmaceutical Exporter",
    initials: "CF",
    rating: 5,
  },
  {
    quote:
      "We were preparing for AEO-T2 and had been struggling with documentation requirements. Glolex structured our compliance manual, trained our team, and got us approved in under six months.",
    author: "Head of Supply Chain",
    role: "Electronics Manufacturer",
    initials: "HS",
    rating: 5,
  },
  {
    quote:
      "The offshoring model works seamlessly. Their India team is our de facto customs and EPR compliance department — handling classification, FTA CoOs, CPCB filings, and broker oversight for 23 countries.",
    author: "VP Procurement",
    role: "European Retail Group",
    initials: "VP",
    rating: 5,
  },
  {
    quote:
      "We had no idea our FSSAI licence category was wrong until Glolex flagged it. They sorted the re-categorisation and DGFT advance authorisation redemption in the same engagement.",
    author: "Compliance Head",
    role: "FMCG Importer",
    initials: "CH",
    rating: 5,
  },
];

// ─── Case Studies ──────────────────────────────────────────────────────────────
export const CASE_STUDIES = [
  {
    id: "pharma-drawback",
    slug: "pharma-drawback-recovery",
    industry: "Pharmaceuticals & Life Sciences",
    industryIcon: "💊",
    service: "Duty Drawback",
    serviceTag: "Duty Drawback",
    headline: "₹4.2 Crore Drawback Recovered for Pharma Exporter",
    subHeadline: "Three years of unclaimed export entitlements unlocked in a single engagement",
    metrics: [
      { value: "₹4.2 Cr", label: "Drawback Recovered" },
      { value: "3 Years", label: "Exports Reviewed" },
      { value: "< 30 Days", label: "ROI Realised" },
    ],
    challenge:
      "A mid-size pharmaceutical exporter with consistent export volumes had never systematically filed for duty drawback on their active pharmaceutical ingredients and formulations. Their internal team assumed the amounts were negligible. Three years of exports had accumulated — with no drawback claims filed.",
    solution:
      "Glolex conducted a full retrospective export audit covering all shipping bills, HS classifications, and applicable drawback rate schedules. We identified classification mismatches, corrected them, and prepared and submitted all outstanding drawback claims with supporting documentation. We also redesigned the team's ongoing drawback process to ensure future claims are filed within the statutory window.",
    result:
      "₹4.2 crore in previously unclaimed duty drawback was recovered. The engagement ROI was realised within the first month. The client's ongoing annual drawback collections now run on a structured, audit-proof process managed by Glolex.",
    quote:
      "Glolex identified ₹4.2 crore in unclaimed drawback from our last three years of exports. The ROI on their engagement was clear within the first month.",
    author: "CFO",
    role: "Mid-size Pharmaceutical Exporter",
    initials: "CF",
  },
  {
    id: "aeo-electronics",
    slug: "aeo-certification-electronics",
    industry: "Electronics & Technology",
    industryIcon: "💻",
    service: "AEO Certification",
    serviceTag: "AEO-T2 Certification",
    headline: "AEO-T2 Certification Achieved in Under 6 Months",
    subHeadline: "Compliance manual, team training, and customs authority approval — end to end",
    metrics: [
      { value: "AEO-T2", label: "Tier Achieved" },
      { value: "< 6 Months", label: "Approval Timeline" },
      { value: "40%+", label: "Reduction in Inspection Rate" },
    ],
    challenge:
      "An electronics manufacturer preparing for AEO-T2 certification had stalled after an initial self-assessment. The documentation requirements were complex, the compliance manual was incomplete, and internal teams lacked clarity on customs authority expectations. Previous attempts had resulted in queries and delays.",
    solution:
      "Glolex took end-to-end ownership of the AEO-T2 application. We developed a comprehensive compliance manual tailored to customs authority requirements, conducted structured training sessions for the import, logistics, and finance teams, mapped all internal processes to AEO criteria, and managed the entire submission and query-response cycle with the customs authority.",
    result:
      "AEO-T2 certification was granted in under six months — significantly faster than industry average. Post-certification, the client immediately benefited from reduced inspection rates, faster port clearance, and enhanced credibility with their global supply chain partners.",
    quote:
      "We were preparing for AEO-T2 and had been struggling with documentation requirements. Glolex structured our compliance manual, trained our team, and got us approved in under six months.",
    author: "Head of Supply Chain",
    role: "Electronics Manufacturer",
    initials: "HS",
  },
  {
    id: "offshoring-retail",
    slug: "trade-compliance-offshoring-europe",
    industry: "Retail & Consumer Goods",
    industryIcon: "🛒",
    service: "Trade Compliance Offshoring",
    serviceTag: "Offshoring",
    headline: "23-Country Trade Compliance Delivered as a Managed Service",
    subHeadline: "Full-spectrum customs, EPR, FTA, and classification support — embedded in the client's operations",
    metrics: [
      { value: "23", label: "Countries Covered" },
      { value: "3 Functions", label: "Compliance, FTA & EPR" },
      { value: "1 Team", label: "Dedicated India Desk" },
    ],
    challenge:
      "A European retail group importing and distributing across 23 countries needed India-specific trade compliance expertise covering customs classification, FTA Certificate of Origin management, EPR (Extended Producer Responsibility) filings, and customs broker oversight — without the cost of building an in-house India team.",
    solution:
      "Glolex established a dedicated India trade compliance desk, acting as the client's embedded customs and regulatory team. Responsibilities include: HS classification reviews, FTA eligibility and CoO management across multiple agreements, CPCB and EPR compliance filings, and day-to-day customs broker oversight and clearance monitoring.",
    result:
      "The client now operates a scalable, cost-efficient compliance function covering 23 markets without an in-house India team. Classification accuracy has improved materially. FTA benefits are now systematically captured. EPR filings are on-time and audit-ready.",
    quote:
      "The offshoring model works seamlessly. Their India team is our de facto customs and EPR compliance department — handling classification, FTA CoOs, CPCB filings, and broker oversight for 23 countries.",
    author: "VP Procurement",
    role: "European Retail Group",
    initials: "VP",
  },
  {
    id: "fmcg-fssai-dgft",
    slug: "fssai-dgft-compliance-fmcg",
    industry: "FMCG / Consumer Goods",
    industryIcon: "🏷️",
    service: "FSSAI & DGFT Advisory",
    serviceTag: "FSSAI + DGFT",
    headline: "FSSAI Re-categorisation and DGFT Redemption in One Engagement",
    subHeadline: "A compliance gap that could have triggered licence cancellation — resolved before a show-cause notice arrived",
    metrics: [
      { value: "2 Licences", label: "Rectified & Redeemed" },
      { value: "1 Engagement", label: "End-to-End Resolution" },
      { value: "0 Penalties", label: "Liability Avoided" },
    ],
    challenge:
      "An FMCG importer was operating under an incorrect FSSAI licence category — a mismatch between their actual product range and the category they held. Separately, their DGFT advance authorisation had expired without proper redemption, creating a latent duty liability. Neither issue had been flagged by their existing compliance team.",
    solution:
      "Glolex conducted a full regulatory health-check of the client's import compliance posture. We identified the FSSAI mis-categorisation and managed the re-categorisation process with the authority. Simultaneously, we audited the advance authorisation ledger, compiled the required export obligation evidence, and completed the DGFT redemption filing before the liability crystallised.",
    result:
      "Both regulatory issues were resolved in a single, structured engagement. No show-cause notices, no penalties, no licence suspension. The client now runs scheduled compliance reviews with Glolex to pre-empt similar gaps.",
    quote:
      "We had no idea our FSSAI licence category was wrong until Glolex flagged it. They sorted the re-categorisation and DGFT advance authorisation redemption in the same engagement.",
    author: "Compliance Head",
    role: "FMCG Importer",
    initials: "CH",
  },
];

// ─── Industries ────────────────────────────────────────────────────────────────
export const INDUSTRIES = [
  {
    id: "electronics",
    icon: "💻",
    title: "Electronics & Technology",
    desc: "BIS/CRS, WPC licensing, HS classification of complex components, AEO advisory, semiconductor import planning.",
  },
  {
    id: "pharma",
    icon: "💊",
    title: "Pharmaceuticals & Life Sciences",
    desc: "CDSCO import licensing, API classification, FSSAI interface, cold-chain customs advisory.",
  },
  {
    id: "automotive",
    icon: "🚗",
    title: "Automotive & Auto Components",
    desc: "FTA utilisation, MOOWR for component manufacturing, drawback for export-linked units.",
  },
  {
    id: "defence",
    icon: "🛡️",
    title: "Defence & Aerospace",
    desc: "SCOMET licensing, ITAR/EAR compliance, project imports, NOC/MOD advisory.",
  },
  {
    id: "chemicals",
    icon: "🧪",
    title: "Chemicals & Petrochemicals",
    desc: "Hazmat import compliance, tariff engineering, REACH, CPCB authorisations for hazardous wastes.",
  },
  {
    id: "fmcg",
    icon: "🛒",
    title: "FMCG / Consumer Goods",
    desc: "FSSAI licensing, EPR (plastic/e-waste), BIS QCO compliance, duty drawback for exporters.",
  },
  {
    id: "logistics",
    icon: "📦",
    title: "Logistics & 3PL",
    desc: "AEO-LO certification, bonded warehouse setup, MOOWR facilitation, EPR advisory for clients.",
  },
  {
    id: "gems",
    icon: "💎",
    title: "Gems & Jewellery",
    desc: "Drawback claims, DGFT REP licence, replenishment advisory, valuation compliance.",
  },
  {
    id: "energy",
    icon: "⚡",
    title: "Renewable Energy & Capital Goods",
    desc: "Project import duty concessions, MOOWR for manufacturing, FTA for equipment sourcing.",
  },
];


// ─── Why Us ───────────────────────────────────────────────────────────────────
export const WHY_US = [
  {
    icon: "🎯",
    title: "Specialists, Not Generalists",
    desc: "We do one thing — customs and trade. This focus means unmatched depth, sharper insight, and faster results.",
  },
  {
    icon: "💼",
    title: "Commercial, Not Just Compliant",
    desc: "Every recommendation is stress-tested against your business objectives, not just the rulebook.",
  },
  {
    icon: "📄",
    title: "Written Opinions",
    desc: "We back our positions with documented legal reasoning — giving you an audit trail that protects against scrutiny.",
  },
  {
    icon: "📡",
    title: "Proactive Intelligence",
    desc: "We monitor regulatory changes and alert you before they affect your shipments — not after the fact.",
  },
  {
    icon: "🏢",
    title: "One-Stop Trade Desk",
    desc: "Customs, DGFT, BIS, EPR, government agencies — handled through one team with full accountability.",
  },
  {
    icon: "💡",
    title: "Transparent Pricing",
    desc: "Fixed-fee and retainer models with clearly defined scope. No billing surprises.",
  },
];

// ─── Inquiry Topics ───────────────────────────────────────────────────────────
export const INQUIRY_TOPICS = [
  "Customs & Trade Compliance",
  "Duty Optimisation",
  "FTA Advisory",
  "Duty Drawback",
  "MOOWR",
  "Export Control / SCOMET",
  "AEO",
  "BIS Compliance",
  "HS Classification",
  "EPR Compliance",
  "DGFT Licensing",
  "Government Agency Licensing",
  "Offshoring / Managed Services",
  "Other",
];

// ─── Insights / Blog ──────────────────────────────────────────────────────────
export const INSIGHTS = [
  {
    id: "india-uae-cepa",
    title: "India-UAE CEPA: Are You Leaving Duty Savings on the Table?",
    excerpt:
      "The India-UAE CEPA has been in force since 2022, yet many importers and exporters are yet to claim their entitled duty concessions. Here is what you need to know.",
    category: "FTA Advisory",
    date: "April 2025",
    readTime: "6 min read",
  },
  {
    id: "moowr-eou-sez",
    title: "MOOWR vs. EOU vs. SEZ: Which Scheme Is Right for Your Manufacturing Setup?",
    excerpt:
      "Three powerful duty relief schemes, three very different compliance frameworks. This comparison guide helps you choose the right structure for your manufacturing unit.",
    category: "MOOWR",
    date: "March 2025",
    readTime: "8 min read",
  },
  {
    id: "epr-2025",
    title: "EPR Compliance in 2025: What Every Importer Must Know About CPCB's Tightening Enforcement",
    excerpt:
      "CPCB has dramatically increased its enforcement actions under EPR. This article covers the key compliance obligations, common pitfalls and what to do if you receive a notice.",
    category: "EPR Compliance",
    date: "March 2025",
    readTime: "7 min read",
  },
  {
    id: "hs-audit",
    title: "HS Classification Audit: How to Find Money You Didn't Know You Were Owed",
    excerpt:
      "A retrospective HS classification audit is one of the most reliable ways to uncover unclaimed duty savings and drawback claims. Here is how to run one.",
    category: "HS Classification",
    date: "February 2025",
    readTime: "5 min read",
  },
  {
    id: "export-controls-dual-use",
    title: "Export Controls in the Age of Dual-Use Technology: What Indian Manufacturers Must Know",
    excerpt:
      "Geopolitical tensions and technology restrictions are raising the stakes for SCOMET compliance. This guide explains your obligations as an Indian exporter of dual-use goods.",
    category: "Export Control",
    date: "February 2025",
    readTime: "9 min read",
  },
  {
    id: "aeo-business-case",
    title: "AEO Certification: The Business Case Beyond Compliance",
    excerpt:
      "AEO is not just a compliance tick-box. We explain the tangible commercial benefits — faster clearance, reduced bank guarantees and MRA advantages — and how to build the business case.",
    category: "AEO",
    date: "January 2025",
    readTime: "6 min read",
  },
  {
    id: "dgft-advance-auth",
    title: "DGFT Advance Authorisation: Avoiding the Most Common Redemption Mistakes",
    excerpt:
      "Advance Authorisation is one of the most valuable export incentive schemes — and one of the most commonly mismanaged. Here are the top redemption mistakes and how to avoid them.",
    category: "DGFT",
    date: "January 2025",
    readTime: "7 min read",
  },
  {
    id: "bis-qco-tracker",
    title: "BIS QCO Tracker: New Categories Effective in 2025–26",
    excerpt:
      "Multiple new Quality Control Orders take effect in 2025–26. We track the latest additions and what they mean for importers in electronics, chemicals and consumer goods.",
    category: "BIS Compliance",
    date: "December 2024",
    readTime: "5 min read",
  },
  {
    id: "govt-clearance-matrix",
    title: "Government Agency Clearance Matrix: Why Your Port Delays Are Avoidable",
    excerpt:
      "Port delays caused by regulatory agency clearances are often avoidable with advance planning. This clearance matrix maps out the key agencies and timelines for common product categories.",
    category: "Government Agencies",
    date: "December 2024",
    readTime: "6 min read",
  },
];

// ─── Insight Category to Service Slug Mapping ─────────────────────────────────
export const CATEGORY_TO_SERVICE_SLUG = {
  "FTA Advisory": "free-trade-agreements",
  "MOOWR": "moowr",
  "EPR Compliance": "epr-compliance",
  "HS Classification": "hs-classification",
  "Export Control": "export-control-scomet",
  "AEO": "aeo",
  "DGFT": "dgft-licensing",
  "BIS Compliance": "bis-compliance",
  "Government Agencies": "government-agencies",
};
